import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCheckoutViewModel } from '../viewmodels/useCheckoutViewModel';
import { Address } from '../types';
import { Icon } from './checkout/icons';

interface OrderReviewProps {
  checkout: ReturnType<typeof useCheckoutViewModel>;
}

const OrderReview: React.FC<OrderReviewProps> = ({ checkout }) => {
  const navigate = useNavigate();
  const [isCompletingOrder, setIsCompletingOrder] = useState(false);

  const {
    checkoutData,
    cart,
    orderSummary,
    goToPreviousStep,
    completeOrder,
    errors,
  } = checkout;

  const formatPrice = (amount: number, currency: string) => {
    const locale = currency.toLowerCase() === 'sek' ? 'sv-SE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatAddress = (address: Address) => {
    const parts = [
      address.first_name + ' ' + address.last_name,
      address.company,
      address.address_1,
      address.address_2,
      `${address.city}, ${address.province} ${address.postal_code}`,
      address.country_code,
    ].filter(Boolean);

    return parts.join('\n');
  };

  const handleCompleteOrder = async () => {
    setIsCompletingOrder(true);

    try {
      console.log('[OrderReview] Starting order completion...');
      const order = await completeOrder();
      console.log('[OrderReview] Order result:', order);

      if (order) {
        console.log('[OrderReview] Navigating to confirmation:', order.id);
        // Navigate to order confirmation page, passing order data via state
        // This avoids needing to re-fetch from the API (which requires authentication)
        navigate(`/order-confirmation/${order.id}`, { state: { order } });
      } else {
        // Don't show alert - the error message is already displayed in the error-banner
        // via the errors.general state set by completeOrder
        console.error('[OrderReview] Order completion returned null - check errors.general for details');
      }
    } catch (error) {
      console.error('[OrderReview] Order completion error:', error);
      // Show alert only for unexpected exceptions
      alert(`Order error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCompletingOrder(false);
    }
  };

  if (!cart || !orderSummary) {
    return <div>Loading...</div>;
  }

  return (
    <div className="order-review" data-testid="order-review">
      <div className="review-section">
        <h2>Review Your Order</h2>
        
        {/* Contact Information */}
        <div className="review-item">
          <div className="review-header">
            <h3>Contact Information</h3>
            <button 
              onClick={() => navigate('/checkout?step=shipping')}
              className="btn btn-link"
            >
              Edit
            </button>
          </div>
          <div className="review-content">
            <p>{checkoutData.email}</p>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="review-item">
          <div className="review-header">
            <h3>Shipping Address</h3>
            <button 
              onClick={() => navigate('/checkout?step=shipping')}
              className="btn btn-link"
            >
              Edit
            </button>
          </div>
          <div className="review-content">
            <pre>{formatAddress(checkoutData.shipping_address)}</pre>
          </div>
        </div>

        {/* Billing Address */}
        {!checkoutData.same_as_shipping && (
          <div className="review-item">
            <div className="review-header">
              <h3>Billing Address</h3>
              <button 
                onClick={() => navigate('/checkout?step=shipping')}
                className="btn btn-link"
              >
                Edit
              </button>
            </div>
            <div className="review-content">
              <pre>{formatAddress(checkoutData.billing_address)}</pre>
            </div>
          </div>
        )}

        {/* Shipping Method */}
        <div className="review-item">
          <div className="review-header">
            <h3>Shipping Method</h3>
            <button 
              onClick={() => navigate('/checkout?step=payment')}
              className="btn btn-link"
            >
              Edit
            </button>
          </div>
          <div className="review-content">
            {checkoutData.shipping_method ? (
              <div>
                <p><strong>{checkoutData.shipping_method.name}</strong></p>
                <p>{checkoutData.shipping_method.description}</p>
                <p>
                  {checkoutData.shipping_method.price === 0 
                    ? 'FREE' 
                    : formatPrice(checkoutData.shipping_method.price, checkoutData.shipping_method.currency_code)
                  }
                </p>
              </div>
            ) : (
              <p>No shipping method selected</p>
            )}
          </div>
        </div>

        {/* Payment Method */}
        <div className="review-item">
          <div className="review-header">
            <h3>Payment Method</h3>
            <button 
              onClick={() => navigate('/checkout?step=payment')}
              className="btn btn-link"
            >
              Edit
            </button>
          </div>
          <div className="review-content">
            <div className="payment-method-info">
              {checkoutData.payment_method?.provider_id.includes('stripe') ? (
                <>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon name="credit-card" size={18} />
                    Stripe Payment (Card, Klarna, Swish, etc.)
                  </span>
                  <div className="payment-security" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon name="shield-check" size={14} />
                    Secure payment processed by Stripe
                  </div>
                </>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Icon name="package" size={18} />
                  Pay on Delivery / Manual Payment
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="review-item">
          <div className="review-header">
            <h3>Order Items ({cart.items.length})</h3>
          </div>
          <div className="review-content">
            <div className="order-items">
              {cart.items.map((item) => (
                <div key={item.id} className="order-item">
                  <div className="item-details">
                    <h4>{item.product.title}</h4>
                    {item.variant.title && (
                      <p className="variant-title">{item.variant.title}</p>
                    )}
                    <p className="item-quantity">Qty: {item.quantity}</p>
                  </div>
                  <div className="item-price">
                    {formatPrice(item.unit_price * item.quantity, cart.currency_code)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="order-totals">
        <div className="total-line">
          <span>Subtotal</span>
          <span>{formatPrice(orderSummary.subtotal, orderSummary.currency)}</span>
        </div>
        <div className="total-line">
          <span>Shipping</span>
          <span>
            {orderSummary.shipping === 0 
              ? 'FREE' 
              : formatPrice(orderSummary.shipping, orderSummary.currency)
            }
          </span>
        </div>
        <div className="total-line">
          <span>Tax</span>
          <span>{formatPrice(orderSummary.tax, orderSummary.currency)}</span>
        </div>
        <div className="total-line total-final">
          <span><strong>Total</strong></span>
          <span><strong>{formatPrice(orderSummary.total, orderSummary.currency)}</strong></span>
        </div>
      </div>

      {errors.general && (
        <div className="error-banner" data-testid="order-error">
          <strong>Payment Error:</strong> {typeof errors.general === 'string' ? errors.general : JSON.stringify(errors.general)}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          onClick={goToPreviousStep}
          className="btn btn-secondary"
          disabled={isCompletingOrder}
        >
          Back to Payment
        </button>
        <button
          type="button"
          onClick={handleCompleteOrder}
          className="btn btn-primary btn-large"
          disabled={isCompletingOrder}
          data-testid="complete-order"
        >
          {isCompletingOrder ? 'Processing...' : 'Complete Order'}
        </button>
      </div>
    </div>
  );
};

export default OrderReview;